import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { patchState, signalStoreFeature, type, withHooks, withMethods, withState } from '@ngrx/signals';
import { find, get } from 'lodash';
import { filter } from 'rxjs';
import { Channel, ChannelMemberResponse, NewMemberPayload } from 'stream-chat';
import { ChannelService, ChatClientService } from 'stream-chat-angular';
import { userState } from '../user/user.feature';

type StaffMemberState = {
  staff_member: ChannelMemberResponse | undefined;
  staff_member_image_url: string | undefined;
  staff_member_online: boolean | undefined;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function withActiveChannelStaffMember<_>() {
  return signalStoreFeature(
    { state: type<userState>() },
    withState<StaffMemberState>({
      staff_member: undefined,
      staff_member_image_url: undefined,
      staff_member_online: undefined,
    }),
    withMethods((store) => ({
      updateStaffMember: async (channel: Channel | undefined) => {
        const userId = store.user()?.doc_id || '';
        const staffMember = await setStaffMemberFromChannel(userId, channel);
        patchState(store, { staff_member: staffMember });
        patchState(store, { staff_member_image_url: setStaffMemberImageUrl(staffMember) });
        patchState(store, { staff_member_online: setStaffMemberOnline(staffMember) });
      },
    })),
    withHooks((store) => ({
      onInit: () => {
        const channelService = inject(ChannelService);
        const chatClientService = inject(ChatClientService);
        const destroyRef = inject(DestroyRef);

        channelService.activeChannel$.pipe(takeUntilDestroyed(destroyRef)).subscribe((channel) => {
          store.updateStaffMember(channel);
        });

        // Listen to events related to staff member changes
        chatClientService.events$
          .pipe(
            filter((e) => {
              const events = ['member.added', 'member.updated', 'member.removed', 'user.presence.changed'];
              return events.includes(e.eventType) && e.event.channel_id === channelService.activeChannel?.id;
            }),
            takeUntilDestroyed(destroyRef)
          )
          .subscribe({
            next: () => {
              store.updateStaffMember(channelService.activeChannel);
            },
          });
      },
    }))
  );
}

async function setStaffMemberFromChannel(userId: string, channel: Channel | undefined) {
  const staffMembers = (await channel?.queryMembers({}))?.members;
  const staffMember =
    find(staffMembers, (member) => {
      const id = typeof member === 'string' ? member : member?.user_id;
      return id !== userId && id !== 'DR_JOII_AI' && id !== 'VET_AI';
    }) ||
    find(staffMembers, (member) => {
      const id = typeof member === 'string' ? member : member?.user_id;
      return id === 'DR_JOII_AI';
    });
  return staffMember as ChannelMemberResponse | NewMemberPayload;
}

function setStaffMemberImageUrl(staffMember: string | ChannelMemberResponse | undefined) {
  return get(staffMember, 'user.image') || '/assets/svgs/joii-logo.svg';
}

function setStaffMemberOnline(staffMember: string | ChannelMemberResponse | NewMemberPayload | undefined) {
  return get(staffMember, 'user.online') || false;
}
